import React from "react";
import ResearchArticle, {
  Body,
  Figure,
  List,
  Notes,
  Points,
  Section,
  Standout,
  Timeline,
} from "../components/portfolio/ResearchArticle";
import { relatedArticles } from "../data/articles";
import imgAiAnalysis from "../assets/images/homepage/ai/analysis.webp";
import imgAiRecommendation from "../assets/images/homepage/ai/recommendation.webp";
import imgAiClosedTicket from "../assets/images/homepage/ai/closed-ticket.webp";
import imgAiLifecycle from "../assets/images/aisdlc-lifecycle.webp";

// AI workflow — a merchant-support workflow where AI prepared the case and a
// specialist kept the decision, shipped in early 2026, and the AI product
// development lifecycle (AI PDLC) that grew out of it. A short read meant to
// earn a conversation: the problem, oversight per step, the interaction
// contract told through the screens that broke it, the lifecycle, and the
// result. Skills, agents and the Teams view in detail are for the call.
//
// Claim boundaries are load-bearing and must survive any edit. −60% workload
// and +10pp CSAT were TARGETS. The results come from the company's support
// analytics against the pre-automation baseline: −68% and +9pp after the
// first month, −72% and +12pp (79% → 91%) after six. The measured claim is
// fewer tickets needing end-to-end manual review, not fewer tickets arriving,
// and it is a different metric from the workload target, so the copy never
// says that target was beaten; only CSAT passed its target. The workshops
// are described by department and headcount (about 30 people), never by a
// workshop count.
//
// The screens are the workspace itself, whole, in a browser window. Names and
// data in them are fictional.

const META = [
  { label: "Role", value: "Product and AI design lead for the workflow and the lifecycle model" },
  { label: "Team", value: "Me, Operations and a software architect" },
  { label: "Product", value: "AI operations workspace, shipped" },
  { label: "When", value: "Early 2026 (workflow), Feb 2026 onward (AI PDLC)" },
];

const OUTCOMES = [
  { value: "−72%", label: "Tickets needing end-to-end manual review, after six months" },
  { value: "+12 pp", label: "Merchant-support CSAT, 79% → 91%" },
  { value: "~30", label: "People across departments in the skills workshops" },
];

const OVERSIGHT = [
  {
    title: "Automated",
    copy: "AI or policy runs the step on its own: retrieve data, assemble evidence, watch a deadline, apply a low-risk rule.",
  },
  {
    title: "Human-in-the-loop",
    copy: "AI prepares the analysis and recommends an action; a specialist approves before anything consequential executes.",
  },
  {
    title: "Human-led",
    copy: "The case is too ambiguous for a recommendation to lead. The specialist investigates; AI helps.",
  },
];

const CONTRACT = [
  {
    title: "The case arrives prepared",
    copy: "The assistant gathers the evidence, writes an analysis, proposes a next action and leaves the sources open. The specialist accepts, revises, rejects or replaces it.",
  },
  {
    title: "The action contradicted the recommendation",
    copy: "On an early screen the assistant recommended asking the merchant for more information, while the primary action still read Block user. Now the primary action is the one recommended.",
  },
  {
    title: "The record didn’t name the decision",
    copy: "A later trail said only “You accepted.” Now it names each one: “You requested additional information.”",
  },
];

const LIFECYCLE = [
  {
    when: "Dec 2025",
    title: "Engineering starts an AI SDLC",
    copy: "It helped ship software, but Research, Product, Design and Analytics sat outside it.",
  },
  {
    when: "Feb 2026",
    title: "I propose the AI PDLC",
    copy: "The same pattern as the support workflow, across the whole lifecycle: AI prepares, a person accepts, and the accepted output becomes the next input.",
  },
  {
    title: "Workshops by department",
    copy: "Around 30 people from risk, product, frontend, backend and more listed the work they actually repeated, then turned it into skills, and skills into workflows.",
  },
  {
    when: "Today",
    title: "How the company builds with AI",
    copy: "Research, Product and Design produce approved specs that engineering builds from; people outside engineering run their own skills; decisions are logged and reviewed.",
  },
];

const PROOF = [
  "Oversight should follow consequence, not AI maturity.",
  "Start from the work people actually repeat, not from the agents you could build.",
  "Accepted decisions become memory, and memory becomes the next phase’s input.",
];

const NOTES = [
  "−60% manual workload and +10pp CSAT were the targets set at the start.",
  "Results come from the company’s support analytics, against the pre-automation baseline: −68% and +9pp after the first month, −72% and +12pp after six.",
  "The measured claim is fewer tickets requiring end-to-end manual review — not fewer tickets arriving.",
  "Names and data in the screens are fictional.",
];

function CaseStudyAiWorkflow() {
  return (
    <ResearchArticle
      eyebrow="Case study"
      title="Designing how people and AI work together"
      lead="A merchant-support workflow where AI prepared the evidence and specialists kept the decision. Six months after it shipped, 72% fewer tickets needed end-to-end manual review, and its pattern became the base of the company’s AI product development lifecycle."
      outcomes={OUTCOMES}
      meta={META}
      hero={{
        src: imgAiAnalysis,
        frame: "browser",
        alt: "The AI operations workspace with a suspicious transaction flagged by the Risk Assistant: the alert details, the activity behind it — four deposits totalling 18,500 dollars in 30 minutes — and the three decisions left to the specialist: request information, mark the user as not suspicious, or block the user",
        label: "The prepared case",
        caption:
          "The evidence in view, the assistant’s reading of it, and the decision left to the specialist.",
      }}
      related={relatedArticles("/case-study/ai-workflow")}
    >
      <Section heading="Specialists assembled every case by hand" tocLabel="The problem">
        <Body>
          Operations specialists handled merchant transaction-support tickets
          end to end: gather evidence, check history, chase documents, review
          what came back, decide. That left little time for the merchants who
          needed a person. The targets were to cut manual workload by 60% and
          lift CSAT by 10 points. I owned the product and AI-design side; a
          software architect owned whether each step could run safely.
        </Body>
      </Section>

      <Section heading="Every step got its own oversight mode" tocLabel="Oversight">
        <Body>
          With Operations and the architect, we took each ticket apart step by
          step and asked the same question of every one: can it run on its
          own, does a person approve before it executes, or does a specialist
          lead? One ticket could then automate the gathering and keep a human
          gate on the decision.
        </Body>
        <Points items={OVERSIGHT} />
      </Section>

      <Section heading="The contract had to be literal" tocLabel="The contract">
        <Body>
          AI prepared the case; people owned the decision. Two early screens
          broke that, and the fixes made it literal.
        </Body>
        <Timeline items={CONTRACT} />
        <Figure
          src={imgAiRecommendation}
          frame="browser"
          alt="The Risk Assistant’s recommendation for a flagged 5,000 dollar transaction: request more information from the merchant to verify the end user’s identity and source of funds. The primary action is Request information, beside Show alert details"
          label="Recommendation and action, aligned"
          caption="The primary action is now the one the assistant recommends."
        />
        <Figure
          src={imgAiClosedTicket}
          frame="browser"
          alt="A resolved ticket’s trail in the workspace: the suspicious transaction flagged; You requested additional information; the information request sent, with the email to the merchant asking for proof of identity and source of funds; the merchant’s response received and analyzed; and the alert closed, with John Doe marked as not suspicious"
          label="A trail that names each decision"
          caption="The gathering ran on its own, the decisions waited for a person, and the follow-up ran after approval."
        />
        <Standout>
          AI can prepare and continue bounded work. Consequential actions need
          legible evidence, an explicit human decision, and a record of what
          was approved.
        </Standout>
      </Section>

      <Section heading="From one workflow to a lifecycle" tocLabel="Lifecycle">
        <Timeline items={LIFECYCLE} />
        <Figure
          src={imgAiLifecycle}
          alt="Five lifecycle phases: Research, Product Definition, Design, AI SDLC and Analytics"
          label="The path around development"
          caption="Each phase converges on a human-approved spec before the next starts. Engineering’s lifecycle stays inside it."
        />
      </Section>

      <Section heading="Better after six months than after one" tocLabel="Results">
        <Body>
          After the first month, tickets needing end-to-end manual review were
          down 68% and CSAT was up 9 points. Six months in, both had kept
          improving, to −72% and +12 points, with CSAT past its +10-point
          target. Specialists spent less time assembling cases and more on
          exceptions and merchants, and kept authority over consequential
          actions.
        </Body>
      </Section>

      <Section heading="What this proves" tocLabel="Takeaways">
        <List items={PROOF} />
      </Section>

      <Notes items={NOTES} />
    </ResearchArticle>
  );
}

export default CaseStudyAiWorkflow;
